import React, { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Menu, X, Octagon } from 'lucide-react';
import { useThemeContext } from '../../context/ThemeContext';
import type { NavbarProps } from '../../types';

const isQrHostname = typeof window !== 'undefined' && window.location.hostname === 'qr.e8software.com';

// The apps pages live on qr.e8software.com; the main domain focuses on services.
const mainNavLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' }
];

const qrNavLinks = [
  { path: '/', label: 'Home' },
  { path: '/our-apps', label: 'Our Apps' },
  { path: '/apps', label: 'App Showcase' }
];

const APP_STORE_URL =
  'https://apps.apple.com/us/app/qr-safe-scanner/id6745104475?ign-itscg=30200&ign-itsct=apps_box_badge&mttnsubad=6745104475&platform=iphone';

const Navbar: React.FC<NavbarProps> = ({
  toggleMobileMenu,
  isMobileMenuOpen
}) => {
  const location = useLocation();
  const { setTheme, isDark } = useThemeContext();

  const isQrDomain = isQrHostname;
  const navLinks = isQrDomain ? qrNavLinks : mainNavLinks;
  
  const cycleTheme = useCallback(() => {
    setTheme(isDark ? 'light' : 'dark');
  }, [isDark, setTheme]);

  // Animation variants
  const logoVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5 } }
  };

  const linkVariants = {
    initial: { opacity: 0, y: -20 },
    animate: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.3,
        delay: 0.1 * custom
      }
    })
  };

  const mobileMenuVariants = {
    closed: { 
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren" 
      }
    },
    open: { 
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const mobileItemVariants = {
    closed: { opacity: 0, x: -20 },
    open: { opacity: 1, x: 0 }
  };

  const ThemeIcon = () => (isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />);

  return (
    <nav className="fixed w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg z-50 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <motion.div 
            className="flex items-center"
            initial="initial"
            animate="animate"
            variants={logoVariants}
          >
            <Link to="/" className="flex items-center">
              <Octagon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">E8 Software</span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.path}
                custom={index}
                initial="initial"
                animate="animate"
                variants={linkVariants}
              >
                <Link
                  to={link.path}
                  className="relative group"
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                >
                  <span className={`
                    ${location.pathname === link.path
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    } transition-colors duration-200`}
                  >
                    {link.label}
                  </span>
                  <motion.div
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400 transform origin-left"
                    initial={{ scaleX: 0 }}
                    animate={{
                      scaleX: location.pathname === link.path ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {isQrDomain ? (
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm font-semibold"
                >
                  Download Now
                </a>
              ) : (
                <Link
                  to="/#contact"
                  className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-semibold"
                >
                  Book consultation
                </Link>
              )}
            </motion.div>
            <motion.button
              onClick={cycleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ThemeIcon />
            </motion.button>
          </div>

          {/* Mobile Navigation Button */}
          <div className="md:hidden flex items-center space-x-2">
            <motion.button
              onClick={cycleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ThemeIcon />
            </motion.button>
            
            <motion.button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              whileTap={{ scale: 0.95 }}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
            className="md:hidden bg-white dark:bg-gray-800 shadow-lg overflow-hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <motion.div
                  key={link.path}
                  variants={mobileItemVariants}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to={link.path}
                    className={`
                      block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200
                      ${location.pathname === link.path
                        ? 'bg-blue-50 dark:bg-gray-700 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                    onClick={toggleMobileMenu}
                    aria-current={location.pathname === link.path ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div variants={mobileItemVariants} whileTap={{ scale: 0.97 }}>
                {isQrDomain ? (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mx-3 mt-2 text-center px-3 py-2 rounded-md text-base font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors duration-200"
                    onClick={toggleMobileMenu}
                  >
                    Download Now
                  </a>
                ) : (
                  <Link
                    to="/#contact"
                    className="block mx-3 mt-2 text-center px-3 py-2 rounded-md text-base font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
                    onClick={toggleMobileMenu}
                  >
                    Book consultation
                  </Link>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default React.memo(Navbar);